import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { Link } from 'react-router-dom';
import { isAlphaInviteOnlyUi } from './alpha-invite-ui.js';
import { useAuth } from './AuthProvider.js';
import { GoogleContinueButton } from './GoogleContinueButton.js';
import { RegisterLegalConsent } from './RegisterLegalConsent.js';
import { RegisterAsModelLink } from './RegisterAsModelLink.js';
const fieldStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    marginBottom: '0.85rem',
};
const ADJECTIVES = [
    'velvet',
    'shadow',
    'neon',
    'crimson',
    'midnight',
    'electric',
    'satin',
    'onyx',
];
const NOUNS = ['fox', 'panther', 'kitten', 'raven', 'viper', 'wolf', 'sphinx', 'cobra'];
function suggestUsername() {
    const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)] ?? 'velvet';
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)] ?? 'fox';
    const suffix = Math.floor(Math.random() * 90) + 10;
    return `${adjective}_${noun}${suffix}`;
}
function resolveIntentCta(intent) {
    const fallback = {
        eyebrow: t('common.auth.adEyebrow'),
        title: t('common.auth.registerPromoTitle'),
        features: [
            t('common.auth.registerPromoFeature1'),
            t('common.auth.registerPromoFeature2'),
            t('common.auth.registerPromoFeature3'),
            t('common.auth.registerPromoFeature4'),
        ],
        ctaRegister: t('common.auth.submitRegister'),
        ctaLogin: t('common.auth.submitLogin'),
    };
    if (!intent || intent === 'generic') {
        return fallback;
    }
    const prefix = `common.auth.cta.${intent}`;
    return {
        eyebrow: t(`${prefix}.eyebrow`),
        title: t(`${prefix}.title`),
        features: [
            t(`${prefix}.feature1`),
            t(`${prefix}.feature2`),
            t(`${prefix}.feature3`),
        ],
        ctaRegister: t(`${prefix}.ctaRegister`),
        ctaLogin: t(`${prefix}.ctaLogin`),
    };
}
export function AuthModal({ isOpen, initialMode = 'register', intent = null, intentMessage = null, isReturningVisitor = false, onClose, onSuccess, }) {
    const { login, register } = useAuth();
    const titleId = useId();
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setMode(initialMode);
        setError(null);
        setSubmitting(false);
    }, [initialMode, isOpen]);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen, onClose]);
    const handleSuggestUsername = useCallback(() => {
        setUsername(suggestUsername());
    }, []);
    async function handleSubmit(event) {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            if (mode === 'register') {
                if (!acceptedTerms) {
                    setError(t('common.auth.termsRequired'));
                    return;
                }
                await register({ email, username, password });
            }
            else {
                await login({ email, password });
            }
            onSuccess();
            onClose();
        }
        catch {
            setError(mode === 'register' ? t('common.auth.registerError') : t('common.auth.loginError'));
        }
        finally {
            setSubmitting(false);
        }
    }
    if (!isOpen) {
        return null;
    }
    const inviteOnly = isAlphaInviteOnlyUi(import.meta.env);
    const isRegister = mode === 'register';
    const cta = resolveIntentCta(intent);
    const visitorBanner = isReturningVisitor
        ? t('common.auth.visitorReturning')
        : t('common.auth.visitorFirst');
    const panelTitle = inviteOnly && isRegister
        ? t('common.auth.inviteOnlyTitle')
        : isRegister
            ? t('common.auth.registerTitle')
            : t('common.auth.loginTitle');
    return createPortal(_jsxs("div", { className: `auth-modal${intent ? ` auth-modal--intent-${intent}` : ''}`, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, children: [_jsx("button", { type: "button", className: "auth-modal__backdrop", "aria-label": t('common.auth.modalClose'), onClick: onClose }), _jsxs("div", { className: "auth-modal__sheet", children: [_jsx("div", { className: "auth-modal__handle", "aria-hidden": true }), _jsxs("div", { className: "auth-modal__layout", children: [_jsxs("aside", { className: "auth-modal__ad", "aria-label": t('common.auth.adLabel'), children: [_jsx("div", { className: "auth-modal__ad-media", "aria-hidden": true }), _jsxs("div", { className: "auth-modal__ad-copy", children: [_jsx("p", { className: "auth-modal__ad-eyebrow", children: cta.eyebrow }), _jsx("h3", { className: "auth-modal__ad-title", children: cta.title }), _jsx("ul", { className: "auth-modal__ad-list", children: cta.features.map((feature) => (_jsx("li", { children: feature }, feature))) })] })] }), _jsxs("div", { className: "auth-modal__panel", children: [_jsxs("header", { className: "auth-modal__header", children: [_jsx("h2", { id: titleId, className: "auth-modal__title", children: panelTitle }), _jsx("button", { type: "button", className: "auth-modal__close", onClick: onClose, "aria-label": t('common.auth.modalClose'), children: "\u00D7" })] }), _jsx("p", { className: "auth-modal__visitor", children: visitorBanner }), intentMessage ? _jsx("p", { className: "auth-modal__intent", children: intentMessage }) : null, inviteOnly && isRegister ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "auth-modal__subtitle", children: t('common.auth.inviteOnlyBody') }), _jsx(Link, { className: "auth-modal__switch-btn", to: "/accept-invite", onClick: () => {
                                                    onClose();
                                                }, style: { display: 'inline-block', marginBottom: '1rem' }, children: t('common.auth.inviteOnlyCta') }), _jsxs("p", { className: "auth-modal__switch", children: [t('common.auth.hasAccount'), ' ', _jsx("button", { type: "button", className: "auth-modal__switch-btn", onClick: () => {
                                                            setMode('login');
                                                            setError(null);
                                                        }, children: t('common.nav.signIn') })] })] })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: "auth-modal__subtitle", children: isRegister ? t('common.auth.registerSubtitle') : t('common.auth.loginSubtitle') }), _jsxs("form", { className: "auth-modal__form", onSubmit: (event) => void handleSubmit(event), noValidate: true, children: [isRegister ? (_jsxs("label", { style: fieldStyle, children: [_jsx("span", { children: t('common.auth.username') }), _jsxs("div", { className: "auth-modal__username-row", children: [_jsx("input", { type: "text", autoComplete: "username", required: true, minLength: 3, maxLength: 30, pattern: "[a-zA-Z0-9_]+", value: username, onChange: (event) => {
                                                                            setUsername(event.target.value);
                                                                        }, className: "fm-input auth-modal__username-input" }), _jsx("button", { type: "button", className: "auth-modal__suggest", onClick: handleSuggestUsername, "aria-label": t('common.auth.suggestUsername'), title: t('common.auth.suggestUsername'), children: "\u2726" })] })] })) : null, _jsxs("label", { style: fieldStyle, children: [_jsx("span", { children: t('common.auth.email') }), _jsx("input", { type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => {
                                                                    setEmail(event.target.value);
                                                                }, className: "fm-input" })] }), _jsxs("label", { style: fieldStyle, children: [_jsx("span", { children: t('common.auth.password') }), _jsx("input", { type: "password", autoComplete: isRegister ? 'new-password' : 'current-password', required: true, minLength: 8, value: password, onChange: (event) => {
                                                                    setPassword(event.target.value);
                                                                }, className: "fm-input" })] }), isRegister ? (_jsx(RegisterLegalConsent, { id: "auth-modal-age-consent", checked: acceptedTerms, disabled: submitting, onCheckedChange: setAcceptedTerms })) : null, error ? (_jsx("p", { role: "alert", className: "fm-error", children: error })) : null, _jsx(Button, { label: submitting
                                                            ? t('common.auth.loading')
                                                            : isRegister
                                                                ? cta.ctaRegister
                                                                : cta.ctaLogin, type: "submit", disabled: submitting || (isRegister && !acceptedTerms) })] }), _jsx("div", { className: "auth-modal__divider", "aria-hidden": true, children: _jsx("span", {}) }), _jsx(GoogleContinueButton, { disabled: submitting || (isRegister && !acceptedTerms), onSuccess: () => {
                                                    onSuccess();
                                                    onClose();
                                                }, onError: (message) => {
                                                    setError(message);
                                                } }), _jsxs("p", { className: "auth-modal__switch", children: [isRegister ? t('common.auth.hasAccount') : t('common.auth.noAccount'), ' ', inviteOnly && !isRegister ? (_jsx(Link, { to: "/accept-invite", className: "auth-modal__switch-btn", onClick: () => {
                                                            onClose();
                                                        }, children: t('common.nav.haveInvite') })) : (_jsx("button", { type: "button", className: "auth-modal__switch-btn", onClick: () => {
                                                            setMode(isRegister ? 'login' : 'register');
                                                            setError(null);
                                                        }, children: isRegister ? t('common.nav.signIn') : t('common.nav.signUp') }))] }), isRegister ? _jsx(RegisterAsModelLink, {}) : null] }))] })] })] })] }), document.body);
}
//# sourceMappingURL=AuthModal.js.map